import i18next from 'i18next'
import {
  CSSProperties,
  KeyboardEvent,
  PointerEvent as ReactPointerEvent,
  Ref,
  useEffect,
  useRef,
  useState
} from 'react'
import { AiLogo } from './AiLogo'

type Side = 'left' | 'right'
type Position = { side: Side; bottom: number }
type Point = { left: number; top: number }

// Distance from the viewport edges, the same as the old bottom-6 right-6 corner
const EDGE_GAP = 24
// Height of the resting launcher: p-3 around the 26px logo
const LAUNCHER_SIZE = 50
// Movement in px below which a press still counts as a click
const DRAG_THRESHOLD = 5
// Keyboard move step, in px per up or down arrow press
const MOVE_STEP = 40
const DEFAULT_POSITION: Position = { side: 'right', bottom: EDGE_GAP }
export const launcherPositionStorageKey = 'aiChat.launcherPosition'

const isPosition = (value: unknown): value is Position =>
  typeof value === 'object' &&
  value !== null &&
  ((value as Position).side === 'left' || (value as Position).side === 'right') &&
  Number.isFinite((value as Position).bottom)

const readStoredPosition = (): Position => {
  try {
    const stored: unknown = JSON.parse(globalThis.localStorage.getItem(launcherPositionStorageKey) ?? 'null')
    return isPosition(stored) ? { side: stored.side, bottom: clampBottom(stored.bottom) } : DEFAULT_POSITION
  } catch {
    return DEFAULT_POSITION
  }
}

const clampBottom = (bottom: number) =>
  Math.max(EDGE_GAP, Math.min(globalThis.innerHeight - LAUNCHER_SIZE - EDGE_GAP, bottom))

export const AiChatLauncher = ({ ref, onOpen }: { ref: Ref<HTMLButtonElement>; onOpen: () => void }) => {
  const [position, setPosition] = useState(readStoredPosition)
  const [dragPoint, setDragPoint] = useState<Point | null>(null)
  const draggedRef = useRef(false)
  const stopDragRef = useRef<(() => void) | null>(null)

  useEffect(() => () => stopDragRef.current?.(), [])

  const moveTo = (next: Position) => {
    setPosition(next)
    globalThis.localStorage.setItem(launcherPositionStorageKey, JSON.stringify(next))
  }

  const onPointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0 || stopDragRef.current) {
      return
    }
    const { pointerId } = event
    const rect = event.currentTarget.getBoundingClientRect()
    const start = { x: event.clientX, y: event.clientY }
    // The hover label collapses while dragging, so a grab on the label holds the logo by its centre
    const grabX = start.x - rect.left > LAUNCHER_SIZE ? LAUNCHER_SIZE / 2 : start.x - rect.left
    const toPoint = (pointer: PointerEvent): Point => ({
      left: pointer.clientX - grabX,
      top: pointer.clientY - (start.y - rect.top)
    })
    draggedRef.current = false

    const onMove = (pointer: PointerEvent) => {
      if (pointer.pointerId !== pointerId) {
        return
      }
      if (draggedRef.current || Math.hypot(pointer.clientX - start.x, pointer.clientY - start.y) >= DRAG_THRESHOLD) {
        draggedRef.current = true
        setDragPoint(toPoint(pointer))
      }
    }
    const stopDrag = () => {
      globalThis.removeEventListener('pointermove', onMove)
      globalThis.removeEventListener('pointerup', onEnd)
      globalThis.removeEventListener('pointercancel', onEnd)
      stopDragRef.current = null
    }
    const onEnd = (pointer: PointerEvent) => {
      if (pointer.pointerId !== pointerId) {
        return
      }
      stopDrag()
      setDragPoint(null)
      if (draggedRef.current) {
        moveTo({
          side: pointer.clientX < globalThis.innerWidth / 2 ? 'left' : 'right',
          bottom: clampBottom(globalThis.innerHeight - toPoint(pointer).top - rect.height)
        })
      }
    }
    globalThis.addEventListener('pointermove', onMove)
    globalThis.addEventListener('pointerup', onEnd)
    globalThis.addEventListener('pointercancel', onEnd)
    stopDragRef.current = stopDrag
  }

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const next: Position | null =
      event.key === 'ArrowLeft' || event.key === 'ArrowRight'
        ? { ...position, side: event.key === 'ArrowLeft' ? 'left' : 'right' }
        : event.key === 'ArrowUp' || event.key === 'ArrowDown'
          ? {
              ...position,
              bottom: clampBottom(clampBottom(position.bottom) + (event.key === 'ArrowUp' ? MOVE_STEP : -MOVE_STEP))
            }
          : null
    if (next) {
      event.preventDefault()
      moveTo(next)
    }
  }

  // Shrinking the window must not push a launcher stored near the top out of view
  const style: CSSProperties = dragPoint ?? {
    ...(position.side === 'left' ? { left: EDGE_GAP } : { right: EDGE_GAP }),
    bottom: `min(${position.bottom}px, calc(100vh - ${LAUNCHER_SIZE + EDGE_GAP}px))`
  }

  return (
    <button
      ref={ref}
      type="button"
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      onClick={(event) => {
        // The pointer click that ends a drag drops the launcher; keyboard clicks have detail 0 and always open
        const endsDrag = draggedRef.current && event.detail > 0
        draggedRef.current = false
        if (!endsDrag) {
          onOpen()
        }
      }}
      aria-label={i18next.t('aiChat.title')}
      title={i18next.t('aiChat.move')}
      aria-keyshortcuts="ArrowUp ArrowDown ArrowLeft ArrowRight"
      style={style}
      className={`group fixed z-40 flex touch-none select-none items-center rounded-full bg-primary-500 p-3 text-white shadow-dark hover:bg-primary-900 ${
        dragPoint ? 'cursor-grabbing' : 'cursor-pointer'
      }`}
    >
      <AiLogo size={26} />
      {/* The name slides out on hover and keyboard focus; the resting and dragged button is logo only */}
      <span
        className={`max-w-0 overflow-hidden whitespace-nowrap font-semibold transition-all duration-300 ${
          dragPoint ? '' : 'group-hover:ml-2 group-hover:max-w-52 group-focus-visible:ml-2 group-focus-visible:max-w-52'
        }`}
      >
        {i18next.t('aiChat.title')}
      </span>
    </button>
  )
}
